import { isNotDefined } from '@sgme/fp';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type {
  CheckCashParams,
  CheckForwardParams,
  CheckNdfParams,
  CheckSpotParams,
} from '@/store/cash/api/checkCashByTradeCapture.ts';
import { useCheckCashByTradeCaptureMutation } from '@/store/cash/api/index.ts';
import { cashErrorOccurred } from '@/store/cash/slice.ts';
import type { FxCash, FxCashForward, FxCashNdf, FxCashSpot } from '@/store/cash/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { ObjectWithUndefinedPropertiesNullable } from '@/utils/typescript.ts';

// ██████╗  █████╗ ████████╗ █████╗
// ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
// ██║  ██║███████║   ██║   ███████║
// ██║  ██║██╔══██║   ██║   ██╔══██║
// ██████╔╝██║  ██║   ██║   ██║  ██║
// ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

type CashChanges = ObjectWithUndefinedPropertiesNullable<FxCash['properties']> & {
  isNonDeliverable?: boolean;
};

export type CashContextContent = {
  tileId: string;
  isChecking: boolean;
  checkCashWithTradeCapture: (change: CashChanges) => Promise<void>;
};

export const CashContext = createContext<CashContextContent>({
  tileId: '',
  isChecking: false,
  checkCashWithTradeCapture: async (_change: CashChanges) => {},
});

// ██████╗ ██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗██████╗
// ██╔══██╗██╔══██╗██╔═══██╗██║   ██║██║██╔══██╗██╔════╝██╔══██╗
// ██████╔╝██████╔╝██║   ██║██║   ██║██║██║  ██║█████╗  ██████╔╝
// ██╔═══╝ ██╔══██╗██║   ██║╚██╗ ██╔╝██║██║  ██║██╔══╝  ██╔══██╗
// ██║     ██║  ██║╚██████╔╝ ╚████╔╝ ██║██████╔╝███████╗██║  ██║
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═══╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

export function CashProvider(props: { tileId: string; children: ReactNode }) {
  const { tileId, children } = props;

  const dispatch = useAppDispatch();

  const [isChecking, setIsChecking] = useState(false);

  const cashType = useAppSelector((state) => state.cash[props.tileId]?.type);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one checkCashWithTradeCapture request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const [checkCashByTradeCapture] = useCheckCashByTradeCaptureMutation();

  const checkCashWithTradeCapture = useCallback(
    async (changes: CashChanges) => {
      // Add the call to the queue
      // If a request is already running, this one will wait until it completes
      return queue.add(async () => {
        if (isNotDefined(cashType)) {
          return;
        }

        const { isNonDeliverable, ...properties } = changes;

        // Timer to show "checking" state after 500ms
        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        try {
          const checkParams = buildCheckCashParams(tileId, cashType, isNonDeliverable, properties);

          await checkCashByTradeCapture(checkParams)
            .unwrap()
            .catch((requestError) => {
              dispatch(
                cashErrorOccurred({
                  tileId,
                  errorType: 'tile',
                  errorCode: 'TIMEOUT',
                  errorMessage: requestError.message,
                }),
              );
            });
        } finally {
          // Clean up timer and state, even on error
          clearTimeout(checkTimer);
          setIsChecking(false);
        }
      });
    },
    [tileId, cashType, checkCashByTradeCapture, queue],
  );

  if (isNotDefined(cashType)) {
    throw new Error(`Could not find cash with tileId ${tileId}`);
  }

  const content: CashContextContent = useMemo(
    () => ({
      tileId,
      isChecking,
      checkCashWithTradeCapture,
    }),
    [tileId, isChecking, checkCashWithTradeCapture],
  );

  return <CashContext.Provider value={content}>{children}</CashContext.Provider>;
}

function buildCheckCashParams(
  tileId: string,
  cashType: 'FxSpot',
  isNonDeliverable: boolean | undefined,
  properties: ObjectWithUndefinedPropertiesNullable<FxCashSpot['properties']>,
): CheckSpotParams;
function buildCheckCashParams(
  tileId: string,
  cashType: 'FxFwd',
  isNonDeliverable: boolean | undefined,
  properties: ObjectWithUndefinedPropertiesNullable<FxCashForward['properties']>,
): CheckForwardParams;
function buildCheckCashParams(
  tileId: string,
  cashType: 'FxNdf',
  isNonDeliverable: boolean | undefined,
  properties: ObjectWithUndefinedPropertiesNullable<FxCashNdf['properties']>,
): CheckNdfParams;
function buildCheckCashParams(
  tileId: string,
  cashType: FxCash['type'],
  isNonDeliverable: boolean | undefined,
  properties: ObjectWithUndefinedPropertiesNullable<FxCash['properties']>,
): CheckCashParams;
function buildCheckCashParams(
  tileId: string,
  cashType: FxCash['type'],
  isNonDeliverable: boolean | undefined,
  properties: ObjectWithUndefinedPropertiesNullable<FxCash['properties']>,
): CheckCashParams {
  return { tileId, type: cashType, isNonDeliverable, properties } as CheckCashParams;
}

// ██╗  ██╗ ██████╗  ██████╗ ██╗  ██╗███████╗
// ██║  ██║██╔═══██╗██╔═══██╗██║ ██╔╝██╔════╝
// ███████║██║   ██║██║   ██║█████╔╝ ███████╗
// ██╔══██║██║   ██║██║   ██║██╔═██╗ ╚════██║
// ██║  ██║╚██████╔╝╚██████╔╝██║  ██╗███████║
// ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function useCashIsChecking() {
  return useContext(CashContext).isChecking;
}

export function useCheckCashByTradeCapture() {
  return useContext(CashContext).checkCashWithTradeCapture;
}
